<template>
  <div class="error-book">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">错题本</h1>
          <div class="header-shortcuts">
            <div class="shortcut-item" @click="navigateTo('/student/practice')">
              <i class="fa fa-file-text-o"></i>
              <span>专项练习</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 筛选和操作栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <!-- 学科筛选 -->
        <el-select v-model="selectedSubject" placeholder="选择学科" @change="handleSubjectChange">
          <el-option label="全部学科" value=""></el-option>
          <el-option 
            v-for="subject in subjects" 
            :key="subject.value" 
            :label="subject.label" 
            :value="subject.value">
          </el-option>
        </el-select>

        <!-- 知识点筛选 -->
        <el-select v-model="selectedKnowledge" placeholder="选择知识点" @change="handleKnowledgeChange">
          <el-option label="全部知识点" value=""></el-option>
          <el-option 
            v-for="knowledge in knowledgePoints" 
            :key="knowledge.value" 
            :label="knowledge.label" 
            :value="knowledge.value">
          </el-option>
        </el-select>

        <!-- 掌握状态筛选 -->
        <el-select v-model="selectedStatus" placeholder="掌握状态" @change="handleStatusChange">
          <el-option label="全部状态" value=""></el-option>
          <el-option label="已掌握" value="mastered"></el-option>
          <el-option label="未掌握" value="unmastered"></el-option>
        </el-select>

        <!-- 时间筛选 -->
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          @change="handleDateChange">
        </el-date-picker>
      </div>

      <div class="filter-right">
        <!-- 批量操作 -->
        <el-button 
          v-if="selectedErrorIds.length > 0" 
          type="primary" 
          size="small"
          @click="batchPrint">
          批量打印
        </el-button>

        <!-- 视图切换 -->
        <div class="view-toggle">
          <el-button-group>
            <el-button 
              :type="viewMode === 'grid' ? 'primary' : ''" 
              size="small"
              @click="viewMode = 'grid'">
              <i class="fa fa-th"></i>
            </el-button>
            <el-button 
              :type="viewMode === 'list' ? 'primary' : ''" 
              size="small"
              @click="viewMode = 'list'">
              <i class="fa fa-list"></i>
            </el-button>
          </el-button-group>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-text">
        <i class="el-icon-loading"></i>
        正在加载错题数据...
      </div>
    </div>

    <!-- 空数据 -->
    <div v-else-if="!errorList.length" class="empty-container">
      <div class="empty-text">暂无数据</div>
    </div>

    <!-- 错题列表 - 网格视图：按日期分组，一行 5 个 -->
    <div v-else-if="viewMode === 'grid'" class="error-grid-wrap">
      <div
        v-for="group in errorGroups"
        :key="group.date"
        class="error-date-group"
      >
        <div class="date-group-header">
          <span class="date-group-title">{{ group.dateLabel }}</span>
          <el-checkbox
            class="date-select-all"
            :value="isGroupAllSelected(group)"
            :indeterminate="isGroupIndeterminate(group)"
            @input="checked => toggleGroupSelection(group, !!checked)"
            @click.native.stop
          >全选</el-checkbox>
        </div>

        <el-checkbox-group v-model="selectedErrorIds" class="error-grid">
          <div
            v-for="error in group.items"
            :key="error.id"
            class="error-card"
            :class="{
              selected: isErrorSelected(error.id),
              mastered: error && error.status === 'mastered'
            }"
            @click="selectError(error)"
          >
            <!-- 错题图片 -->
            <div class="error-image">
              <img :src="error.imageUrl" :alt="error.subject" @click.stop="viewImage(error.imageUrl)" />
              <div class="image-overlay">
                <i class="fa fa-search-plus"></i>
              </div>
            </div>

            <!-- 选择框放在图片之后，避免被图片/遮罩盖住 -->
            <div class="card-checkbox" @click.stop>
              <el-checkbox :label="error.id">
                <span></span>
              </el-checkbox>
            </div>

            <!-- 错题信息 -->
            <div class="error-info">
              <div class="error-subject">{{ error.subject }}</div>
              <div class="error-knowledge">{{ error.knowledgePoint }}</div>
              <div class="error-time">{{ formatClock(error.createTime) }}</div>

              <!-- 状态标签 -->
              <div class="error-status" :class="error && error.status ? error.status : 'unmastered'">
                {{ error && error.status === 'mastered' ? '已掌握' : '未掌握' }}
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="error-actions">
              <el-button size="mini" type="text" @click.stop="viewDetail(error)">
                <i class="fa fa-eye"></i> 查看
              </el-button>
              <el-button
                size="mini"
                type="text"
                :class="error && error.status === 'mastered' ? 'text-warning' : 'text-success'"
                @click.stop="toggleMastered(error)">
                <i class="fa" :class="error && error.status === 'mastered' ? 'fa-times' : 'fa-check'"></i>
                {{ error && error.status === 'mastered' ? '取消掌握' : '标记掌握' }}
              </el-button>
            </div>
          </div>
        </el-checkbox-group>
      </div>
    </div>

    <!-- 错题列表 - 列表视图 -->
    <div v-else-if="viewMode === 'list'" class="error-list">
      <el-table 
        :data="errorList"
        style="width: 100%"
        @selection-change="handleSelectionChange"
        @row-click="viewDetail">
        <el-table-column type="selection" width="55"></el-table-column>
        
        <el-table-column label="题目图片" width="120">
          <template slot-scope="scope">
            <div class="table-image" @click.stop="viewImage(scope.row.imageUrl)">
              <img :src="scope.row.imageUrl" :alt="scope.row.subject" />
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="subject" label="学科" width="80"></el-table-column>
        <el-table-column prop="knowledgePoint" label="知识点" min-width="200"></el-table-column>
        
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag 
              :type="scope.row && scope.row.status === 'mastered' ? 'success' : 'danger'"
              size="small">
              {{ scope.row && scope.row.status === 'mastered' ? '已掌握' : '未掌握' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="createTime" label="添加时间" min-width="160">
          <template slot-scope="scope">
            {{ formatTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button size="mini" @click.stop="viewDetail(scope.row)">查看</el-button>
            <el-button 
              size="mini" 
              :type="scope.row && scope.row.status === 'mastered' ? 'warning' : 'success'"
              @click.stop="toggleMastered(scope.row)">
              {{ scope.row && scope.row.status === 'mastered' ? '取消掌握' : '标记掌握' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页：有数据时才显示，避免出现「共 0 条」 -->
    <div v-if="!loading && total > 0" class="pagination-wrapper">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 15, 30, 60]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 图片预览模态框 -->
    <el-dialog
      :visible.sync="imagePreviewVisible"
      width="80%"
      center
      :show-close="false"
      @click.native="imagePreviewVisible = false">
      <img :src="previewImageUrl" style="width: 100%; max-height: 70vh; object-fit: contain;" />
    </el-dialog>

    <!-- 错题详情模态框 -->
    <el-dialog
      title="错题详情"
      :visible.sync="detailVisible"
      width="900px"
      center>
      <div v-if="currentError" class="error-detail">
        <div class="detail-content">
          <div class="detail-left">
            <div class="detail-image">
              <img :src="currentError.imageUrl" :alt="currentError.subject" />
            </div>
          </div>
          <div class="detail-right">
            <div class="detail-info">
              <div class="info-item">
                <label>学科：</label>
                <span>{{ currentError.subject }}</span>
              </div>
              <div class="info-item">
                <label>知识点：</label>
                <span>{{ currentError.knowledgePoint }}</span>
              </div>
              <div class="info-item">
                <label>添加时间：</label>
                <span>{{ formatTime(currentError.createTime) }}</span>
              </div>
              <div class="info-item">
                <label>掌握状态：</label>
                <el-tag 
                  :type="currentError && currentError.status === 'mastered' ? 'success' : 'danger'"
                  size="small">
                  {{ currentError && currentError.status === 'mastered' ? '已掌握' : '未掌握' }}
                </el-tag>
              </div>
            </div>
            
            <div class="detail-answer" v-if="currentError.answer">
              <h4>解析：</h4>
              <div class="answer-content" v-html="currentError.answer"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button 
          :type="currentError && currentError.status === 'mastered' ? 'warning' : 'success'"
          @click="toggleMastered(currentError)">
          {{ currentError && currentError.status === 'mastered' ? '取消掌握' : '标记掌握' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { axios } from "../../config/axios";
import moment from 'moment';

export default {
  name: 'ErrorBook',
  data() {
    return {
      // 筛选条件
      selectedSubject: '',
      selectedKnowledge: '',
      selectedStatus: '',
      dateRange: [],
      
      // 选项数据
      subjects: [
        { label: '数学', value: '数学' },
        { label: '语文', value: '语文' },
        { label: '英语', value: '英语' },
        { label: '物理', value: '物理' },
        { label: '化学', value: '化学' }
      ],
      knowledgePoints: [],
      
      // 视图模式
      viewMode: 'grid', // grid | list
      
      // 错题数据
      errorList: [],
      selectedErrorIds: [],
      loading: false,
      
      // 分页
      currentPage: 1,
      pageSize: 15,
      total: 0,
      
      // 模态框
      imagePreviewVisible: false,
      previewImageUrl: '',
      detailVisible: false,
      currentError: null
    }
  },
  computed: {
    errorGroups() {
      const groups = [];
      const indexMap = {};
      this.errorList.forEach(error => {
        const dateKey = moment(error.createTime).format('YYYY-MM-DD');
        if (indexMap[dateKey] === undefined) {
          indexMap[dateKey] = groups.length;
          groups.push({
            date: dateKey,
            dateLabel: moment(error.createTime).format('YYYY年M月D日'),
            items: []
          });
        }
        groups[indexMap[dateKey]].items.push(error);
      });
      return groups;
    }
  },
  created() {
    this.loadErrorBook();
    this.loadKnowledgePoints();
  },
  methods: {
    navigateTo(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    async loadErrorBook() {
      this.loading = true;
      try {
        const params = {
          pageNumber: this.currentPage - 1, // 后端从 0 开始
          pageSize: this.pageSize
        };

        if (this.selectedSubject) {
          params.subject = this.selectedSubject;
        }
        if (this.selectedKnowledge) {
          params.knowledgePoint = this.selectedKnowledge;
        }
        if (this.selectedStatus === 'mastered') {
          params.mastered = 1;
        } else if (this.selectedStatus === 'unmastered') {
          params.mastered = 0;
        }

        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = moment(this.dateRange[0]).format('YYYY-MM-DD');
          params.endDate = moment(this.dateRange[1]).format('YYYY-MM-DD');
        }

        const response = await axios.get('/requestErrorRecord/list', { params });
        const res = response.data;
        if (res.code !== 200) {
          this.errorList = [];
          this.total = 0;
          this.$message.error(res.message || '加载错题本失败');
          return;
        }

        const data = res.data || {};
        this.errorList = (data.list || []).filter(item => item && item.id).map(item => ({
          ...item,
          status: item.mastered ? 'mastered' : 'unmastered',
          subject: item.subject || '未知',
          knowledgePoint: item.knowledgePoint || '未知',
          imageUrl: item.paperCutImg || item.dealImgUrl || '',
          createTime: item.createTime || new Date(),
          answer: item.text || ''
        }));
        this.total = data.total || 0;
      } catch (error) {
        console.error('加载错题本失败:', error);
        this.errorList = [];
        this.total = 0;
        this.$message.error('加载错题本失败');
      } finally {
        this.loading = false;
      }
    },
    
    async loadKnowledgePoints() {
      if (!this.selectedSubject) {
        this.knowledgePoints = [];
        return;
      }
      try {
        const response = await axios.get('/requestErrorRecord/knowledgePointBySubject', {
          params: { subject: this.selectedSubject }
        });
        const list = response.data.data || [];
        this.knowledgePoints = list.map(item => ({
          label: item,
          value: item
        }));
      } catch (error) {
        console.error('加载知识点失败:', error);
        this.knowledgePoints = [];
        this.$message.warning('加载知识点失败');
      }
    },
    
    // 筛选处理
    handleSubjectChange() {
      this.selectedKnowledge = '';
      this.loadKnowledgePoints();
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleKnowledgeChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleStatusChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleDateChange() {
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    // 分页处理
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.loadErrorBook();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadErrorBook();
    },
    
    // 选择处理
    selectError(error) {
      this.currentError = error;
    },

    isErrorSelected(errorId) {
      return this.selectedErrorIds.some(id => id === errorId);
    },

    setErrorSelected(errorId, checked) {
      const index = this.selectedErrorIds.findIndex(id => id === errorId);
      if (checked && index === -1) {
        this.selectedErrorIds.push(errorId);
      } else if (!checked && index > -1) {
        this.selectedErrorIds.splice(index, 1);
      }
    },

    isGroupAllSelected(group) {
      return group.items.length > 0 && group.items.every(item => this.isErrorSelected(item.id));
    },

    isGroupIndeterminate(group) {
      const selectedCount = group.items.filter(item => this.isErrorSelected(item.id)).length;
      return selectedCount > 0 && selectedCount < group.items.length;
    },

    toggleGroupSelection(group, checked) {
      const ids = group.items.map(item => item.id);
      if (checked) {
        const selected = new Set(this.selectedErrorIds);
        ids.forEach(id => selected.add(id));
        this.selectedErrorIds = Array.from(selected);
      } else {
        const removeSet = new Set(ids);
        this.selectedErrorIds = this.selectedErrorIds.filter(id => !removeSet.has(id));
      }
    },
    
    toggleSelection(errorId) {
      this.setErrorSelected(errorId, !this.isErrorSelected(errorId));
    },
    
    handleSelectionChange(selection) {
      this.selectedErrorIds = selection.map(item => item.id);
    },
    
    // 操作处理
    async toggleMastered(error) {
      if (!error || !error.id) {
        this.$message.error('错题数据无效');
        return;
      }
      
      try {
        const currentStatus = error.status || 'unmastered';
        const newStatus = currentStatus === 'mastered' ? 'unmastered' : 'mastered';
        const response = await axios.put(`/requestErrorRecord/${error.id}/status`, {
          status: newStatus
        }, {
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(data) => JSON.stringify(data)]
        });
        const res = response.data;
        if (res.code !== 200) {
          this.$message.error(res.message || '操作失败');
          return;
        }

        error.status = newStatus;
        error.mastered = newStatus === 'mastered';
        this.$message.success(newStatus === 'mastered' ? '已标记为掌握' : '已取消掌握');

      } catch (err) {
        console.error('更新掌握状态失败:', err);
        this.$message.error('操作失败');
      }
    },
    
    async batchPrint() {
      try {
        const response = await axios.post('/requestErrorRecord/batch-print', {
          errorIds: this.selectedErrorIds
        }, {
          headers: { 'Content-Type': 'application/json' },
          transformRequest: [(data) => JSON.stringify(data)]
        });
        const res = response.data || {};
        if (res.code !== 200) {
          this.$message.error(res.message || '打印失败');
          return;
        }
        this.$message.success('打印任务已提交');
        this.selectedErrorIds = [];
      } catch (error) {
        console.error('批量打印失败:', error);
        this.$message.error('打印失败');
      }
    },
    
    viewDetail(error) {
      this.currentError = error;
      this.detailVisible = true;
    },
    
    viewImage(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.imagePreviewVisible = true;
    },
    
    formatTime(time) {
      return moment(time).format('YYYY-MM-DD HH:mm');
    },

    formatClock(time) {
      return moment(time).format('HH:mm');
    }
  }
}
</script>

<style lang="less" scoped>
.error-book {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

// 页面头部
.page-header {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.header-shortcuts {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shortcut-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  i {
    font-size: 14px;
  }

  &:hover {
    background: #f0f5ff;
    color: #3498db;
  }

  &.active {
    background: #ecf5ff;
    color: #3498db;
    font-weight: 600;
  }
}

// 筛选栏
.filter-bar {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-left {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-right {
  display: flex;
  gap: 12px;
  align-items: center;
}

.view-toggle {
  margin-left: 12px;
}

// 网格视图
.error-grid-wrap {
  margin-bottom: 24px;
}

.error-date-group {
  margin-bottom: 20px;
}

.date-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.date-group-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.date-select-all {
  color: #606266;
  font-weight: 500;
}

.error-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  font-size: 14px;
}

.error-card {
  min-width: 0;
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 2px solid transparent;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
  
  &.selected {
    border-color: #3498db;
  }
  
  &.mastered {
    opacity: 0.8;
    
    .error-image::after {
      content: '已掌握';
      position: absolute;
      top: 8px;
      right: 8px;
      background: #27ae60;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
    }
  }
}

.card-checkbox {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 20;
  line-height: 1;
  padding: 3px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.85);

  /deep/ .el-checkbox {
    display: block;
    margin: 0;
  }

  /deep/ .el-checkbox__label {
    display: none;
    padding: 0;
  }

  // 只提亮未选中态，选中态交给 element-ui，否则会盖掉蓝底白勾
  /deep/ .el-checkbox__input:not(.is-checked) .el-checkbox__inner {
    background-color: #fff;
    border-color: #c0c4cc;
  }
}

.error-image {
  width: 100%;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;
    
    i {
      color: white;
      font-size: 24px;
    }
  }
  
  &:hover .image-overlay {
    opacity: 1;
  }
}

.error-info {
  margin-bottom: 12px;
}

.error-subject {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error-knowledge {
  font-size: 13px;
  color: #7f8c8d;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error-time {
  font-size: 12px;
  color: #95a5a6;
  margin-bottom: 8px;
}

.error-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  &.mastered {
    background-color: #d4edda;
    color: #155724;
  }
  
  &.unmastered {
    background-color: #f8d7da;
    color: #721c24;
  }
}

.error-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;

  .el-button {
    flex: 1;
    margin: 0;
    padding: 0 4px;
  }
}

// 列表视图
.error-list {
  width: 100%;
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  /deep/ .el-table {
    width: 100% !important;
  }
}

.table-image {
  width: 80px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// 分页
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px;
}

// 错题详情
.error-detail {
  .detail-content {
    display: flex;
    gap: 24px;
  }
  
  .detail-left {
    flex: 1;
  }
  
  .detail-right {
    flex: 1;
  }
  
  .detail-image {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: auto;
    }
  }
  
  .detail-info {
    margin-bottom: 24px;
  }
  
  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    label {
      font-weight: 500;
      color: #2c3e50;
      min-width: 80px;
    }
  }
  
  .detail-answer {
    h4 {
      margin-bottom: 12px;
      color: #2c3e50;
    }
    
    .answer-content {
      padding: 16px;
      background-color: #f8f9fa;
      border-radius: 8px;
      line-height: 1.6;
    }
  }
}

// 响应式设计
@media (max-width: 1400px) {
  .error-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .error-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .error-book {
    padding: 15px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .header-left {
    flex-direction: column;
    gap: 12px;
  }
  
  .filter-bar {
    flex-direction: column;
    gap: 16px;
  }
  
  .filter-left {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .error-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  .detail-content {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .error-grid {
    grid-template-columns: 1fr;
  }
  
  .filter-left {
    width: 100%;
    
    .el-select, .el-date-editor {
      width: 100%;
      margin-bottom: 8px;
    }
  }
}

// 加载状态
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background: white;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  
  .loading-text {
    color: #7f8c8d;
    font-size: 16px;
    
    i {
      margin-right: 8px;
      animation: rotating 2s linear infinite;
    }
  }
}

// 空数据
.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background: white;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  .empty-text {
    color: #909399;
    font-size: 14px;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 文本颜色类
.text-success {
  color: #67c23a !important;
}

.text-warning {
  color: #e6a23c !important;
}
</style>
